import { defineStore } from 'pinia'
import { ref } from 'vue'
import i18n, { initialLocale, localeOptions, type LocaleCode } from '@/i18n'
import { setCookie } from '@/utils/cookie'

export type ThemeName = 'light' | 'dark' | 'orange' | 'blue'

const themeNames: ThemeName[] = ['light', 'dark', 'orange', 'blue']
// 本地存的主题
const storedTheme = localStorage.getItem('findbao-theme') as ThemeName | null
const configuredTheme = import.meta.env.VITE_APP_DEFAULT_THEME?.trim() as ThemeName | undefined
// 默认主题
const defaultTheme =
  configuredTheme && themeNames.includes(configuredTheme) ? configuredTheme : 'blue'

export const useAppStore = defineStore('app', () => {
  const theme = ref<ThemeName>(
    storedTheme && themeNames.includes(storedTheme) ? storedTheme : defaultTheme,
  )
  const locale = ref<LocaleCode>(initialLocale)
  const mobileMenuOpen = ref(false)
  const sidebarCollapsed = ref(localStorage.getItem('findbao-sidebar') === 'collapsed')

  function applyTheme(value: ThemeName) {
    const root = document.documentElement
    root.classList.toggle('dark', value === 'dark')
    root.classList.toggle('theme-orange', value === 'orange')
    root.classList.toggle('theme-blue', value === 'blue')
    root.dataset.theme = value
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    if (meta)
      meta.content =
        value === 'dark'
          ? '#06141d'
          : value === 'orange'
            ? '#fff8ed'
            : value === 'blue'
              ? '#f3f6fb'
              : '#f6f7f3'
  }

  function applyLocale(value: LocaleCode) {
    const option = localeOptions.find((item) => item.code === value)!
    i18n.global.locale.value = value
    document.documentElement.lang = value
    document.documentElement.dir = option.dir
  }

  function applyPreferences() {
    applyTheme(theme.value)
    applyLocale(locale.value)
  }

  function setTheme(value: ThemeName) {
    theme.value = value
    localStorage.setItem('findbao-theme', value)
    applyTheme(value)
  }

  function setLocale(value: LocaleCode) {
    locale.value = value
    setCookie('locale', value)
    applyLocale(value)
  }

  function toggleSidebar() {
    sidebarCollapsed.value = !sidebarCollapsed.value
    localStorage.setItem('findbao-sidebar', sidebarCollapsed.value ? 'collapsed' : 'open')
  }

  return {
    theme,
    locale,
    mobileMenuOpen,
    sidebarCollapsed,
    applyPreferences,
    setTheme,
    setLocale,
    toggleSidebar,
  }
})
